import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSearch, FiFilter, FiRefreshCw, FiEdit2, FiX, FiSave, FiEye, FiDownload } from 'react-icons/fi';
import { FaGraduationCap, FaUserGraduate, FaChalkboardTeacher, FaFlask, FaLaptopCode, FaHeartbeat, FaBook } from 'react-icons/fa';
import './Lecturers.css';

const Lecturers = () => {
  const [lecturers, setLecturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [selectedLecturer, setSelectedLecturer] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [syncConfig, setSyncConfig] = useState({
    limit: 50,
    syncMode: 'upsert'
  });
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    byProgram: [],
    byJabatan: []
  });

  useEffect(() => {
    fetchLecturers();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, genderFilter]);

  const fetchLecturers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (genderFilter) params.gender = genderFilter;

      const res = await axios.get('/api/lecturers', { params });
      setLecturers(res.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal mengambil data dosen');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get('/api/lecturers/stats');
      setStats(res.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setStatusFilter('');
    setGenderFilter('');
    fetchLecturers();
    fetchStats();
  };

  const handleSyncLecturers = async () => {
    setSyncing(true);
    try {
      const res = await axios.post('/api/neofeeder/sync/lecturers', syncConfig);
      toast.success(res.data.message || 'Sinkronisasi dosen berhasil!');
      setShowSyncModal(false);
      fetchLecturers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Sinkronisasi dosen gagal');
      console.error('Sync error:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleViewDetail = (lecturer) => {
    setSelectedLecturer(lecturer);
    setIsEditMode(false);
    setEditForm({
      name: lecturer.name || '',
      nip: lecturer.nip || '',
      nidn: lecturer.nidn || '',
      email: lecturer.email || '',
      phone: lecturer.phone || '',
      gender: lecturer.gender || '',
      birthPlace: lecturer.birthPlace || '',
      birthDate: lecturer.birthDate ? new Date(lecturer.birthDate).toISOString().split('T')[0] : '',
      address: lecturer.address || '',
      program: lecturer.program || '',
      jabatanAkademik: lecturer.jabatanAkademik || '',
      pendidikanTerakhir: lecturer.pendidikanTerakhir || '',
      status: lecturer.status || 'Aktif',
      isActive: lecturer.isActive !== undefined ? lecturer.isActive : true
    });
    
    // Set photo preview
    if (lecturer.photo && lecturer.photo !== 'default-avatar.png') {
      setPhotoPreview(`/uploads/lecturers/${lecturer.photo}`);
    } else {
      setPhotoPreview('/default-avatar.png');
    }
    
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedLecturer(null);
    setIsEditMode(false);
    setEditForm({});
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handleEditMode = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    // Reset form to original data
    if (selectedLecturer) {
      setEditForm({
        name: selectedLecturer.name || '',
        nip: selectedLecturer.nip || '',
        nidn: selectedLecturer.nidn || '',
        email: selectedLecturer.email || '',
        phone: selectedLecturer.phone || '',
        gender: selectedLecturer.gender || '',
        birthPlace: selectedLecturer.birthPlace || '',
        birthDate: selectedLecturer.birthDate ? new Date(selectedLecturer.birthDate).toISOString().split('T')[0] : '',
        address: selectedLecturer.address || '',
        program: selectedLecturer.program || '',
        jabatanAkademik: selectedLecturer.jabatanAkademik || '',
        pendidikanTerakhir: selectedLecturer.pendidikanTerakhir || '',
        status: selectedLecturer.status || 'Aktif',
        isActive: selectedLecturer.isActive !== undefined ? selectedLecturer.isActive : true
      });
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedLecturer) return;
    
    try {
      await axios.put(`/api/lecturers/${selectedLecturer._id}`, editForm);
      
      // Upload photo if selected
      if (photoFile) {
        const photoFormData = new FormData();
        photoFormData.append('photo', photoFile);
        
        await axios.post(`/api/lecturers/${selectedLecturer._id}/upload-photo`, photoFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      toast.success('Data dosen berhasil diperbarui');
      
      // Update selected lecturer with new data
      setSelectedLecturer({ ...selectedLecturer, ...editForm });
      setIsEditMode(false);
      
      // Refresh list
      fetchLecturers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal memperbarui data dosen');
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('File harus berupa gambar');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Ukuran file maksimal 5MB');
        return;
      }

      setPhotoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(selectedLecturer?.photo && selectedLecturer.photo !== 'default-avatar.png' 
      ? `/uploads/lecturers/${selectedLecturer.photo}` 
      : '/default-avatar.png');
    
    // Reset file input
    const fileInput = document.getElementById('lecturer-photo-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusBadge = (isActive, status) => {
    if (isActive) {
      return <span className="table-badge table-badge-success">Aktif</span>;
    } else {
      return <span className="table-badge table-badge-secondary">{status || 'Tidak Aktif'}</span>;
    }
  };

  // Function to get icon based on program name
  const getProgramIcon = (programName) => {
    const lowerName = programName.toLowerCase();
    
    // Kesehatan & Keperawatan
    if (lowerName.includes('keperawatan') || lowerName.includes('ners')) {
      return { icon: <FaHeartbeat />, color: '#ef4444' }; // Red
    }
    // Kesehatan Masyarakat
    if (lowerName.includes('kesehatan')) {
      return { icon: <FaUserGraduate />, color: '#10b981' }; // Green
    }
    // Teknik & Informatika
    if (lowerName.includes('teknik') || lowerName.includes('informatika') || lowerName.includes('komputer')) {
      return { icon: <FaLaptopCode />, color: '#3b82f6' }; // Blue
    }
    // Sains & Penelitian
    if (lowerName.includes('sains') || lowerName.includes('biologi') || lowerName.includes('kimia') || lowerName.includes('fisika')) {
      return { icon: <FaFlask />, color: '#8b5cf6' }; // Purple
    }
    // Pendidikan
    if (lowerName.includes('pendidikan') || lowerName.includes('guru')) {
      return { icon: <FaChalkboardTeacher />, color: '#f59e0b' }; // Orange
    }
    // Default - General Education
    return { icon: <FaGraduationCap />, color: '#6366f1' }; // Indigo
  };

  if (loading && lecturers.length === 0) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="lecturers-page">
      {/* Modern Transparent Header */}
      <div className="page-header modern-header">
        <div className="header-background"></div>
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon-wrapper">
              <FaChalkboardTeacher className="header-icon" />
            </div>
            <div className="header-text">
              <h1 className="header-title">Data Dosen</h1>
              <p className="header-subtitle">Kelola data dosen dari NEO Feeder</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn btn-glass btn-success" onClick={() => setShowSyncModal(true)}>
              <FiDownload /> 
              <span className="btn-text">Sinkronisasi</span>
            </button>
            <button className="btn btn-glass btn-primary" onClick={handleRefresh}>
              <FiRefreshCw /> 
              <span className="btn-text">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#4f46e5' }}>
            <FiFilter />
          </div>
          <div className="stat-details">
            <h3>{stats.total || 0}</h3>
            <p>Total Dosen</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#10b981' }}>
            <FiRefreshCw />
          </div>
          <div className="stat-details">
            <h3>{stats.active || 0}</h3>
            <p>Dosen Aktif</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#ef4444' }}>
            <FiRefreshCw />
          </div>
          <div className="stat-details">
            <h3>{stats.inactive || 0}</h3>
            <p>Dosen Tidak Aktif</p>
          </div>
        </div>

        {/* Dosen per Program Studi */}
        {stats.byProgram && stats.byProgram.length > 0 && stats.byProgram
          .filter(p => p._id) // Filter out empty program
          .slice(0, 6) // Show top 6 programs
          .map((prog, idx) => {
            const iconData = getProgramIcon(prog._id);
            return (
              <div key={idx} className="program-stat-card">
                <div className="program-stat-content">
                  <div className="program-icon" style={{ background: iconData.color }}>
                    {iconData.icon}
                  </div>
                  <div className="program-info">
                    <div className="program-count">{prog.count}</div>
                    <div className="program-label">Dosen</div>
                  </div>
                </div>
                <div className="program-name" title={prog._id}>
                  {prog._id}
                </div>
              </div>
            );
          })}
        
        {stats.byProgram && stats.byProgram.filter(p => p._id).length > 6 && (
          <div className="program-stat-card program-stat-more">
            <div className="program-stat-content">
              <div className="program-icon" style={{ background: '#6b7280' }}>
                <FaBook />
              </div>
              <div className="program-info">
                <div className="program-count">
                  {stats.byProgram.filter(p => p._id).slice(6).reduce((sum, p) => sum + p.count, 0)}
                </div>
                <div className="program-label">Dosen</div>
              </div>
            </div>
            <div className="program-name">
              {stats.byProgram.filter(p => p._id).length - 6} Program Lainnya
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="filters">
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Cari nama, NIP, atau NIDN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Semua Status</option>
            <option value="Aktif">Aktif</option>
            <option value="Tidak Aktif">Tidak Aktif</option>
            <option value="Pensiun">Pensiun</option>
            <option value="Tugas Belajar">Tugas Belajar</option>
            <option value="Cuti">Cuti</option>
          </select>

          <select
            className="filter-select"
            value={genderFilter}
            onChange={(e) => setGenderFilter(e.target.value)}
          >
            <option value="">Semua Jenis Kelamin</option>
            <option value="L">Laki-laki</option>
            <option value="P">Perempuan</option>
          </select>
        </div>
      </div>

      {/* Lecturers Table */}
      <div className="card">
        <h3>Daftar Dosen</h3>
        {lecturers.length === 0 ? (
          <div className="empty-state">
            <p>
              {searchTerm || statusFilter || genderFilter
                ? 'Tidak ada dosen yang sesuai dengan filter'
                : 'Belum ada data dosen. Silakan lakukan sinkronisasi dari NEO Feeder.'}
            </p>
          </div>
        ) : (
          <div className="modern-table-container">
            <table className="modern-table accent-purple">
              <thead>
                <tr>
                  <th>Foto</th>
                  <th>NIDN</th>
                  <th>Nama Lengkap</th>
                  <th>Program Studi</th>
                  <th>Jabatan</th>
                  <th>Status</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {lecturers.map((lecturer) => (
                  <tr key={lecturer._id}>
                    <td>
                      <div className="lecturer-avatar-table">
                        <img 
                          src={lecturer.photo && lecturer.photo !== 'default-avatar.png' 
                            ? `/uploads/lecturers/${lecturer.photo}` 
                            : '/default-avatar.png'} 
                          alt={lecturer.name} 
                        />
                      </div>
                    </td>
                    <td>{lecturer.nidn || '-'}</td>
                    <td>
                      <strong>{lecturer.name}</strong>
                    </td>
                    <td>{lecturer.program || '-'}</td>
                    <td>{lecturer.jabatanAkademik || '-'}</td>
                    <td>{getStatusBadge(lecturer.isActive, lecturer.status)}</td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="table-btn table-btn-view"
                          onClick={() => handleViewDetail(lecturer)}
                          title="Lihat Detail"
                        >
                          <FiEye />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Detail Biodata */}
      {showModal && selectedLecturer && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                {!isEditMode && (
                  <div className="lecturer-avatar-modal">
                    <img src={photoPreview || '/default-avatar.png'} alt="Avatar" />
                  </div>
                )}
                <div>
                  <h3>Detail Biodata Dosen</h3>
                  {!isEditMode && selectedLecturer && (
                    <p style={{ margin: '5px 0 0 0', color: '#64748b', fontSize: '14px' }}>
                      {selectedLecturer.name} - {selectedLecturer.nidn || selectedLecturer.nip || 'Tanpa NIDN/NIP'}
                    </p>
                  )}
                </div>
              </div>
              <div className="modal-header-actions">
                {!isEditMode ? (
                  <button className="btn-edit-modal" onClick={handleEditMode}>
                    <FiEdit2 /> Edit
                  </button>
                ) : (
                  <>
                    <button className="btn-save-modal" onClick={handleSaveEdit}>
                      <FiSave /> Simpan
                    </button>
                    <button className="btn-cancel-modal" onClick={handleCancelEdit}>
                      <FiX /> Batal
                    </button>
                  </>
                )}
                <button className="modal-close" onClick={handleCloseModal}>
                  <FiX />
                </button>
              </div>
            </div>
            <div className="modal-body">
              {/* Photo Section */}
              {isEditMode && (
                <div className="detail-section photo-section-lecturer">
                  <h4 className="section-title">Foto Profil</h4>
                  <div className="photo-upload-container-lecturer">
                    <div className="photo-preview-lecturer">
                      {photoPreview ? (
                        <img src={photoPreview} alt="Preview" />
                      ) : (
                        <div className="photo-placeholder-lecturer">
                          <FiSave style={{ fontSize: '48px', opacity: 0.3 }} />
                          <p>Belum ada foto</p>
                        </div>
                      )}
                    </div>
                    <div className="photo-actions-lecturer">
                      <label htmlFor="lecturer-photo-input" className="btn btn-outline">
                        Pilih Foto
                      </label>
                      <input
                        id="lecturer-photo-input"
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        style={{ display: 'none' }}
                      />
                      {photoPreview && photoPreview !== '/default-avatar.png' && (
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={handleRemovePhoto}
                        >
                          <FiX /> Hapus
                        </button>
                      )}
                      <small className="text-muted">
                        Format: JPG, PNG, GIF (Max 5MB)
                      </small>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Biodata Dasar */}
              <div className="detail-section">
                <h4 className="section-title">Biodata Dasar</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>NIP</label>
                    {isEditMode ? (
                      <input
                        type="text"
                        className="edit-input"
                        value={editForm.nip || ''}
                        onChange={(e) => handleInputChange('nip', e.target.value)}
                      />
                    ) : (
                      <p>{selectedLecturer.nip || '-'}</p>
                    )}
                  </div>
                  <div className="detail-item">
                    <label>NIDN</label>
                    {isEditMode ? (
                      <input
                        type="text"
                        className="edit-input"
                        value={editForm.nidn || ''}
                        onChange={(e) => handleInputChange('nidn', e.target.value)}
                      />
                    ) : (
                      <p>{selectedLecturer.nidn || '-'}</p>
                    )}
                  </div>
                  <div className="detail-item">
                    <label>Nama Lengkap</label>
                    {isEditMode ? (
                      <input
                        type="text"
                        className="edit-input"
                        value={editForm.name || ''}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        required
                      />
                    ) : (
                      <p><strong>{selectedLecturer.name}</strong></p>
                    )}
                  </div>
                  <div className="detail-item">
                    <label>Jenis Kelamin</label>
                    {isEditMode ? (
                      <select
                        className="edit-select"
                        value={editForm.gender || ''}
                        onChange={(e) => handleInputChange('gender', e.target.value)}
                      >
                        <option value="">Pilih</option>
                        <option value="L">Laki-laki</option>
                        <option value="P">Perempuan</option>
                      </select>
                    ) : (
                      <p>{selectedLecturer.gender === 'L' ? 'Laki-laki' : selectedLecturer.gender === 'P' ? 'Perempuan' : '-'}</p>
                    )}
                  </div>
                  <div className="detail-item">
                    <label>Status</label>
                    {isEditMode ? (
                      <select
                        className="edit-select"
                        value={editForm.isActive ? 'Aktif' : editForm.status || 'Tidak Aktif'}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleInputChange('status', value);
                          handleInputChange('isActive', value === 'Aktif');
                        }}
                      >
                        <option value="Aktif">Aktif</option>
                        <option value="Tidak Aktif">Tidak Aktif</option>
                        <option value="Pensiun">Pensiun</option>
                        <option value="Tugas Belajar">Tugas Belajar</option>
                        <option value="Cuti">Cuti</option>
                      </select>
                    ) : (
                      <p>{getStatusBadge(selectedLecturer.isActive, selectedLecturer.status)}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Biodata Detail dari Neo Feeder */}
              {selectedLecturer.biodata && (
                <div className="detail-section">
                  <h4 className="section-title">Biodata Lengkap (Neo Feeder)</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>NIK</label>
                      <p>{selectedLecturer.biodata.nik || '-'}</p>
                    </div>
                    <div className="detail-item">
                      <label>Tempat, Tanggal Lahir</label>
                      <p>{selectedLecturer.biodata.tempatLahir || '-'}, {formatDate(selectedLecturer.biodata.tanggalLahir)}</p>
                    </div>
                    <div className="detail-item">
                      <label>Agama</label>
                      <p>{selectedLecturer.biodata.agama || '-'}</p>
                    </div>
                    <div className="detail-item">
                      <label>Status Perkawinan</label>
                      <p>{selectedLecturer.biodata.statusPerkawinan || '-'}</p>
                    </div>
                    <div className="detail-item">
                      <label>Nama Ibu Kandung</label>
                      <p>{selectedLecturer.biodata.namaIbuKandung || '-'}</p>
                    </div>
                    <div className="detail-item">
                      <label>Email</label>
                      <p>{selectedLecturer.biodata.email || '-'}</p>
                    </div>
                    <div className="detail-item">
                      <label>Handphone</label>
                      <p>{selectedLecturer.biodata.handphone || '-'}</p>
                    </div>
                    <div className="detail-item">
                      <label>Telepon</label>
                      <p>{selectedLecturer.biodata.telepon || '-'}</p>
                    </div>
                    <div className="detail-item full-width">
                      <label>Alamat</label>
                      <p>{selectedLecturer.biodata.alamat || '-'} RT {selectedLecturer.biodata.rt || '-'} / RW {selectedLecturer.biodata.rw || '-'}, {selectedLecturer.biodata.kelurahan || '-'} {selectedLecturer.biodata.kodePos || ''}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Riwayat Pendidikan */}
              {selectedLecturer.riwayatPendidikan && selectedLecturer.riwayatPendidikan.length > 0 && (
                <div className="detail-section">
                  <h4 className="section-title">Riwayat Pendidikan</h4>
                  <div className="table-wrapper">
                    <table className="detail-table">
                      <thead>
                        <tr>
                          <th>Jenjang</th>
                          <th>Institusi</th>
                          <th>Bidang Studi</th>
                          <th>Gelar</th>
                          <th>Tahun</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedLecturer.riwayatPendidikan.map((edu, idx) => (
                          <tr key={idx}>
                            <td>{edu.namaJenjangPendidikan || '-'}</td>
                            <td>{edu.namaInstitusi || '-'}</td>
                            <td>{edu.bidangStudi || '-'}</td>
                            <td>{edu.gelar || '-'}</td>
                            <td>{edu.tahunMasuk || '-'} - {edu.tahunLulus || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Penugasan */}
              {selectedLecturer.penugasan && selectedLecturer.penugasan.length > 0 && (
                <div className="detail-section">
                  <h4 className="section-title">Penugasan Dosen</h4>
                  <div className="table-wrapper">
                    <table className="detail-table">
                      <thead>
                        <tr>
                          <th>Program Studi</th>
                          <th>No. Surat Tugas</th>
                          <th>TMT Tugas</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedLecturer.penugasan.map((pen, idx) => (
                          <tr key={idx}>
                            <td>{pen.namaProdi || '-'}</td>
                            <td>{pen.nomorSuratTugas || '-'}</td>
                            <td>{formatDate(pen.tmtTugas)}</td>
                            <td><span className="table-badge table-badge-info">{pen.statusKeaktifan || '-'}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Aktivitas Mengajar */}
              {selectedLecturer.aktivitasMengajar && selectedLecturer.aktivitasMengajar.length > 0 && (
                <div className="detail-section">
                  <h4 className="section-title">Aktivitas Mengajar</h4>
                  <div className="table-wrapper">
                    <table className="detail-table">
                      <thead>
                        <tr>
                          <th>Mata Kuliah</th>
                          <th>SKS</th>
                          <th>Semester</th>
                          <th>Pertemuan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedLecturer.aktivitasMengajar.slice(0, 10).map((act, idx) => (
                          <tr key={idx}>
                            <td>{act.namaSubstansi || '-'} ({act.kodeSubstansi || '-'})</td>
                            <td>{act.sks || '-'}</td>
                            <td>{act.namaSemester || '-'}</td>
                            <td>{act.pertemuanRealisasi || 0} / {act.pertemuanDirencanakan || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {selectedLecturer.aktivitasMengajar.length > 10 && (
                      <p className="text-muted" style={{marginTop: '10px', fontSize: '13px'}}>
                        Menampilkan 10 dari {selectedLecturer.aktivitasMengajar.length} aktivitas mengajar
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Riwayat Jabatan Fungsional */}
              {selectedLecturer.riwayatJabatanFungsional && selectedLecturer.riwayatJabatanFungsional.length > 0 && (
                <div className="detail-section">
                  <h4 className="section-title">Riwayat Jabatan Fungsional</h4>
                  <div className="table-wrapper">
                    <table className="detail-table">
                      <thead>
                        <tr>
                          <th>Jabatan</th>
                          <th>No. SK</th>
                          <th>TMT</th>
                          <th>Kredit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedLecturer.riwayatJabatanFungsional.map((jab, idx) => (
                          <tr key={idx}>
                            <td>{jab.namaJabatanFungsional || '-'}</td>
                            <td>{jab.nomorSk || '-'}</td>
                            <td>{formatDate(jab.tmtJabatanFungsional)}</td>
                            <td>{jab.kredit || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Riwayat Pangkat */}
              {selectedLecturer.riwayatPangkat && selectedLecturer.riwayatPangkat.length > 0 && (
                <div className="detail-section">
                  <h4 className="section-title">Riwayat Pangkat/Golongan</h4>
                  <div className="table-wrapper">
                    <table className="detail-table">
                      <thead>
                        <tr>
                          <th>Pangkat/Golongan</th>
                          <th>No. SK</th>
                          <th>TMT</th>
                          <th>Masa Kerja</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedLecturer.riwayatPangkat.map((pkt, idx) => (
                          <tr key={idx}>
                            <td>{pkt.namaPangkat || '-'}</td>
                            <td>{pkt.nomorSk || '-'}</td>
                            <td>{formatDate(pkt.tmtPangkat)}</td>
                            <td>{pkt.masaKerjaGolonganTahun || 0} tahun {pkt.masaKerjaGolonganBulan || 0} bulan</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Riwayat Sertifikasi */}
              {selectedLecturer.riwayatSertifikasi && selectedLecturer.riwayatSertifikasi.length > 0 && (
                <div className="detail-section">
                  <h4 className="section-title">Riwayat Sertifikasi</h4>
                  <div className="table-wrapper">
                    <table className="detail-table">
                      <thead>
                        <tr>
                          <th>Jenis Sertifikasi</th>
                          <th>Bidang Studi</th>
                          <th>No. Registrasi</th>
                          <th>Tahun</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedLecturer.riwayatSertifikasi.map((sert, idx) => (
                          <tr key={idx}>
                            <td>{sert.jenisSertifikasi || '-'}</td>
                            <td>{sert.bidangStudi || '-'}</td>
                            <td>{sert.nomorRegistrasi || '-'}</td>
                            <td>{sert.tahunSertifikasi || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Sync Config */}
      {showSyncModal && (
        <div className="modal-overlay" onClick={() => setShowSyncModal(false)}>
          <div className="modal-content modal-sync" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Sinkronisasi Data Dosen</h3>
              <button className="modal-close" onClick={() => setShowSyncModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="sync-info">
                <p>Mengambil data dosen lengkap dari Neo Feeder termasuk:</p>
                <ul>
                  <li>✓ Biodata lengkap dosen</li>
                  <li>✓ Riwayat penugasan</li>
                  <li>✓ Aktivitas mengajar</li>
                  <li>✓ Riwayat jabatan fungsional</li>
                  <li>✓ Riwayat pangkat/golongan</li>
                  <li>✓ Riwayat pendidikan</li>
                  <li>✓ Riwayat sertifikasi</li>
                </ul>
              </div>

              <div className="form-group">
                <label>Mode Sinkronisasi</label>
                <select
                  className="form-control"
                  value={syncConfig.syncMode}
                  onChange={(e) => setSyncConfig({ ...syncConfig, syncMode: e.target.value })}
                >
                  <option value="upsert">Upsert (Insert + Update)</option>
                  <option value="insert">Insert Only (Tambah Baru)</option>
                  <option value="update">Update Only (Update Existing)</option>
                </select>
                <small className="form-text">
                  {syncConfig.syncMode === 'upsert' && '✓ Tambah data baru atau update jika sudah ada'}
                  {syncConfig.syncMode === 'insert' && '✓ Hanya tambah data baru, skip yang sudah ada'}
                  {syncConfig.syncMode === 'update' && '✓ Hanya update data yang sudah ada, skip yang baru'}
                </small>
              </div>

              <div className="form-group">
                <label>Jumlah Data (Limit)</label>
                <input
                  type="number"
                  className="form-control"
                  value={syncConfig.limit}
                  onChange={(e) => setSyncConfig({ ...syncConfig, limit: parseInt(e.target.value) || 50 })}
                  min="1"
                  max="1000"
                />
                <small className="form-text">
                  Jumlah data dosen yang akan disinkronkan (1-1000)
                  <br />
                  <strong>Estimasi waktu:</strong> ~3-5 detik per dosen
                </small>
              </div>

              <div className="alert alert-warning">
                <strong>⚠️ Perhatian:</strong>
                <ul style={{ marginBottom: 0 }}>
                  <li>Setiap dosen akan mengambil 8 API calls dari Neo Feeder</li>
                  <li>Untuk {syncConfig.limit} dosen: estimasi {Math.ceil(syncConfig.limit * 4 / 60)} - {Math.ceil(syncConfig.limit * 5 / 60)} menit</li>
                  <li>Disarankan mulai dengan 10-20 dosen untuk testing</li>
                </ul>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowSyncModal(false)}
              >
                Batal
              </button>
              <button
                className="btn btn-success"
                onClick={handleSyncLecturers}
                disabled={syncing}
              >
                {syncing ? (
                  <>
                    <div className="spinner-small"></div>
                    Sinkronisasi...
                  </>
                ) : (
                  <>
                    <FiDownload /> Mulai Sinkronisasi
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Lecturers;
